import React, { useEffect, useState } from 'react'
import { Container, Nav, NavItem, NavLink, TabContent, TabPane, Alert } from 'reactstrap'
import { useQuery } from '@apollo/client'
import { useParams, useNavigate } from 'react-router-dom'
import { IS_INBOX_ADMIN } from './Queries'
import InboxManagement from './InboxManagement'
import ChannelManagement from './ChannelManagement'
import InboxMemberManagement from './InboxMemberManagement'
import MessageTemplateManagement from './MessageTemplateManagement'
import './CommsAdmin.css'

const TABS = {
    INBOXES: 'inboxes',
    CHANNELS: 'channels',
    MEMBERS: 'members',
    TEMPLATES: 'templates',
}

export default function CommsAdminContainer() {
    const { tabName } = useParams()
    const navigate = useNavigate()
    const sanitizedTabName = Object.values(TABS).includes(tabName) ? tabName : TABS.INBOXES
    const [activeTab, setActiveTab] = useState(sanitizedTabName)

    const { data, loading, error } = useQuery(IS_INBOX_ADMIN, {
        fetchPolicy: 'network-only'
    })

    const isAdmin = data?.isInboxAdmin

    useEffect(() => {
        if (sanitizedTabName !== activeTab) {
            setActiveTab(sanitizedTabName)
        }
    }, [sanitizedTabName, activeTab])

    const handleTabChange = (tab) => {
        if (tab === activeTab) {
            return
        }
        setActiveTab(tab)
        if (tab === TABS.INBOXES) {
            navigate('/comms/admin')
        } else {
            navigate(`/comms/admin/${tab}`)
        }
    }


    if (loading) {
        return (
            <Container fluid className="comms-admin-container">
                <div className="comms-admin-hero loading">
                    <div className="comms-admin-hero-copy">
                        <span className="skeleton-block skeleton-chip" />
                        <span className="skeleton-block skeleton-title" />
                        <span className="skeleton-block skeleton-text" />
                    </div>
                </div>

                <div className="comms-admin-card">
                    <div className="comms-admin-tabs loading">
                        {[0, 1, 2].map((index) => (
                            <span key={index} className="skeleton-block skeleton-pill" />
                        ))}
                    </div>
                    <div className="comms-admin-tab-content loading">
                        {[0, 1, 2, 3].map((index) => (
                            <div key={index} className="skeleton-block skeleton-row" />
                        ))}
                    </div>
                </div>
            </Container>
        )
    }

    if (error) {
        return (
            <Container fluid className="comms-admin-container">
                <Alert color="danger">
                    Error loading admin panel: {error.message}
                </Alert>
            </Container>
        )
    }

    if (!isAdmin) {
        return (
            <Container fluid className="comms-admin-container">
                <Alert color="warning">
                    <h4 className="alert-heading">Access Denied</h4>
                    <p>
                        You do not have admin permissions for any inbox. 
                        Please contact an administrator to grant you admin access.
                    </p>
                </Alert>
            </Container>
        )
    }

    return (
        <Container fluid className="comms-admin-container">
            <Nav tabs className="comms-admin-tabs">
                    <NavItem>
                        <NavLink
                            className={activeTab === TABS.INBOXES ? 'active' : ''}
                            onClick={() => handleTabChange(TABS.INBOXES)}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="fa fa-inbox" aria-hidden="true" /> Inboxes
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={activeTab === TABS.CHANNELS ? 'active' : ''}
                            onClick={() => handleTabChange(TABS.CHANNELS)}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="fa fa-comments" aria-hidden="true" /> Channels
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={activeTab === TABS.MEMBERS ? 'active' : ''}
                            onClick={() => handleTabChange(TABS.MEMBERS)}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="fa fa-users" aria-hidden="true" /> Members
                        </NavLink>
                    </NavItem>
                    <NavItem>
                        <NavLink
                            className={activeTab === TABS.TEMPLATES ? 'active' : ''}
                            onClick={() => handleTabChange(TABS.TEMPLATES)}
                            style={{ cursor: 'pointer' }}
                        >
                            <i className="fa fa-sticky-note" aria-hidden="true" /> Templates
                        </NavLink>
                    </NavItem>
                </Nav>
            <div className="comms-admin-card">
                

                <TabContent activeTab={activeTab} className="comms-admin-tab-content">
                    <TabPane tabId={TABS.INBOXES}>
                        {activeTab === TABS.INBOXES && <InboxManagement />}
                    </TabPane>
                    <TabPane tabId={TABS.CHANNELS}>
                        {activeTab === TABS.CHANNELS && <ChannelManagement />}
                    </TabPane>
                    <TabPane tabId={TABS.MEMBERS}>
                        {activeTab === TABS.MEMBERS && <InboxMemberManagement />}
                    </TabPane>
                    <TabPane tabId={TABS.TEMPLATES}>
                        {activeTab === TABS.TEMPLATES && <MessageTemplateManagement />}
                    </TabPane>
                </TabContent>
            </div>
        </Container>
    )
}
